/**
 * @file SubscriptionPanel
 * @description 「订阅管理」Tab 内容
 * 展示订阅管理列表：当前生效中 + 待生效两个分组，每个分组下逐条渲染卡片
 */

import {useMemo} from 'react';
import {EMPTY_ICON} from '../constants';
import {useSubscriptionDetail} from '../hooks/useSubscriptionDetail';
import styles from './index.module.less';
import type {MemberLevel} from '@/services/commerce/type';
import Button from '@/components/common/Button';
import StatusView from '@/components/common/StatusView';
import GradientLevelText from '@/commerce/components/GradientLevelText';
import {createCx} from '@/utils/cx';
import {formatUnixDate} from '@/utils/date';
import {fenToYuan} from '@/utils/money';
import {openMembershipModal} from '@/commerce/modals/openModal';
import {MembershipModalSource} from '@/commerce/modals/MembershipModal/types';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';

const cx = createCx(styles);

interface RowProps {
    label: string;
    children: React.ReactNode;
}

const Row: React.FC<RowProps> = ({label, children}) => (
    <div className={cx('row')}>
        <span className={cx('label')}>{label}</span>
        <span className={cx('value')}>{children}</span>
    </div>
);

/** 生效时间区间，格式 2026-08-11至2026-09-11（"至"连接、无空格） */
const dateRange = (startTime: number, endTime: number) =>
    `${formatUnixDate(startTime)}至${formatUnixDate(endTime)}`;

/**
 * 订阅卡片展示字段：当前生效中与待生效的接口结构略有差异，
 * 统一映射成这组展示字段后由 SubscriptionCard 渲染
 */
interface SubscriptionCardData {
    memberLevel: MemberLevel;
    planName: string;
    startTime: number;
    endTime: number;
    payChannel: string;
    /** 下次续费时间（Unix 秒）；不适用时为 null，展示为 - */
    nextRenewTime: number | null;
    /** 下次续费金额（分）；不适用时为 null，展示为 - */
    nextRenewAmount: number | null;
    /** 续费状态行文案；不适用时为 null，展示为 - */
    statusDesc: string | null;
}

/**
 * 两张卡片共同的数据源形状：CurrentSubscription 不适用时下发 null，
 * SubscriptionCardItem 不适用时不下发该字段，故三个续费字段都按可选 + 可空处理
 */
interface SubscriptionCardSource {
    memberLevel: MemberLevel;
    levelName: string;
    subscriptionName: string;
    startTime: number;
    endTime: number;
    payChannel: string;
    nextRenewTime?: number | null;
    nextRenewAmount?: number | null;
    statusDesc?: string | null;
}

/**
 * 卡片数据映射，当前生效中与待生效共用。
 * 续费信息与续费状态文案是否适用一律由服务端判定，前端只做空值兜底，不再二次判断订阅类型，
 * 避免两端口径不一致。
 */
const toCardData = (item: SubscriptionCardSource): SubscriptionCardData => ({
    memberLevel: item.memberLevel,
    planName: `${item.levelName}·${item.subscriptionName}`,
    startTime: item.startTime,
    endTime: item.endTime,
    payChannel: item.payChannel,
    nextRenewTime: item.nextRenewTime ?? null,
    nextRenewAmount: item.nextRenewAmount ?? null,
    statusDesc: item.statusDesc ?? null,
});

interface SubscriptionCardProps {
    data: SubscriptionCardData;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({data}) => {
    const {
        memberLevel,
        planName,
        startTime,
        endTime,
        payChannel,
        nextRenewTime,
        nextRenewAmount,
        statusDesc,
    } = data;

    return (
        <div className={cx('card')}>
            <Row label="订阅类型">
                <GradientLevelText className={cx('planName')} memberLevel={memberLevel}>
                    {planName}
                </GradientLevelText>
            </Row>
            <Row label="生效时间">{dateRange(startTime, endTime)}</Row>
            <Row label="下次续费时间">
                {nextRenewTime === null ? '-' : formatUnixDate(nextRenewTime)}
            </Row>
            <Row label="下次续费金额">
                {nextRenewAmount === null ? '-' : `¥${fenToYuan(nextRenewAmount)}`}
            </Row>
            <Row label="支付方式">{payChannel}</Row>
            <Row label="续费状态">{statusDesc ?? '-'}</Row>
        </div>
    );
};

interface SubscriptionPanelProps {
    enabled: boolean;
}

type PanelStatus = 'loading' | 'error' | 'empty' | 'ready';

const SubscriptionPanel: React.FC<SubscriptionPanelProps> = ({enabled}) => {
    const {currentSubscription, pendingList, isLoading, isError} = useSubscriptionDetail(enabled);
    const {sendClickLog} = useTracking();

    const status = useMemo<PanelStatus>(() => {
        if (isLoading) {
            return 'loading';
        }
        if (isError) {
            return 'error';
        }
        if (currentSubscription || pendingList?.length) {
            return 'ready';
        }
        return 'empty';
    }, [currentSubscription, pendingList, isError, isLoading]);

    if (status === 'empty') {
        return (
            <div className={cx('empty')}>
                <img className={cx('emptyIcon')} src={EMPTY_ICON} alt="" />
                <div className={cx('emptyText')}>当前无订阅</div>
                <Button
                    onClick={() => {
                        // 非会员点击【去订阅会员】埋点
                        sendClickLog({
                            page: TrackEvents.SUBSCRIPTION_MANAGEMENT_POP,
                            value: TrackEvents.SUBSCRIPTION_BTN,
                        });
                        openMembershipModal(
                            MembershipModalSource.SUBSCRIPTION_MANAGEMENT_ORDER_HISTORY_PAGE_MEMBERSHIP_BTN,
                        );
                    }}
                >
                    去订阅会员
                </Button>
            </div>
        );
    }

    if (status !== 'ready') {
        return (
            <div className={cx('placeholder')}>
                <StatusView status={status} size="medium" loadingTheme="white" />
            </div>
        );
    }

    return (
        <div className={cx('container')}>
            <div className={cx('scrollBox')}>
                {currentSubscription && (
                    <div className={cx('group')}>
                        <div className={cx('groupTitle')}>当前生效中</div>
                        <SubscriptionCard data={toCardData(currentSubscription)} />
                    </div>
                )}
                {pendingList && pendingList.length > 0 && (
                    <div className={cx('group')}>
                        <div className={cx('groupTitle')}>待生效</div>
                        {pendingList.map((item) => (
                            <SubscriptionCard key={item.subscriptionId} data={toCardData(item)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPanel;
